import React, { useContext } from 'react';

import styled from 'styled-components';

import { isJobEvent } from '@libs/EventTypes';

import { ListContext } from '@contexts/ListContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import { AcknowledgmentTick } from '@components/AcknowledgmentDot/acknowledgmentTick';

const EventModelNameSpan = styled.span`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.15rem;

    p {
        text-transform: capitalize;
        margin: 0;
        line-height: 1;

        &.is-confirmed {
            font-weight: 700;
        }

        &.is-cancelled {
            ${(props) => props.theme.RedLineThrough(1)}
            color: ${(props) => props.theme.TextColorMuted};
        }

        &.is-confirmed.is-cancelled {
            ${(props) => props.theme.RedLineThrough(2)}
        }
    }

    &::after {
        content: ',';
    }

    &:last-child::after {
        content: '';
    }
`;

const OptionNumberDisc = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background-color: #${(props) => props.$bgcolor};
    color: #${(props) => props.$fgcolor};
    font-size: ${(props) => props.theme.FontSizeXSm};
    font-weight: bold;
    margin-left: 0.25rem;
    vertical-align: middle;
`;

const EventModelName = ({ modelID, model, event, asArray = false }) => {
    const { cachedList } = useContext(ListContext);
    const contactName = getContactName(modelID, asArray);

    if (!contactName) {
        return null;
    }

    // Determine styling classes based on model properties
    const isConfirmed = model?.OptionNumber === 10;
    const isCancelled = model?.Cancel;
    const hasOptionNumber = isJobEvent(event.TypeName) && model?.OptionNumber && model.OptionNumber > 0;
    const isAcknowledgment = model?.Acknowledgment;

    const className = [isConfirmed ? 'is-confirmed' : '', isCancelled ? 'is-cancelled' : ''].filter(Boolean).join(' ');

    // Color calculation logic (same as OptionNumber component)
    const getColors = () => {
        if (!isJobEvent(event.TypeName)) {
            return { bgcolor: 'fadb14', fgcolor: '000000' };
        }
        let eventStyle = null;
        const evtypevalue = event.TypeName
            ? event.TypeName.toLowerCase()
            : event.Type
              ? event.Type.toLowerCase()
              : null;
        const evtypeid = evtypevalue ? (evtypevalue === 'job' ? 20 : evtypevalue === 'ext' ? 30 : 20) : 20;
        const parentEvent = cachedList.eventTypes.items.find((et) => et.id === evtypeid);
        if (event && event.SubTypeCode) {
            eventStyle = parentEvent.subTypes.find((st) => st.Code === event.SubTypeCode);
        } else {
            eventStyle = parentEvent.subTypes.find((st) => st.Default);
        }
        if (eventStyle) {
            let bgcolor = 'f5222d';
            let fgcolor = 'ffffff';
            const number = model?.OptionNumber;
            if (number === 10) {
                if (eventStyle.BackColor) {
                    bgcolor = eventStyle.BackColor;
                }
                if (eventStyle.ForeColor) {
                    fgcolor = eventStyle.ForeColor;
                }
                return { bgcolor, fgcolor };
            } else {
                if (number === 1 && eventStyle.FirstOptionBackColor) {
                    bgcolor = eventStyle.FirstOptionBackColor;
                    fgcolor = eventStyle.FirstOptionForeColor;
                    return { bgcolor, fgcolor };
                }
                if (number === 2 && eventStyle.SecondOptionBackColor) {
                    bgcolor = eventStyle.SecondOptionBackColor;
                    fgcolor = eventStyle.SecondOptionForeColor;
                    return { bgcolor, fgcolor };
                }
                if (number === 3 && eventStyle.ThirdOptionBackColor) {
                    bgcolor = eventStyle.ThirdOptionBackColor;
                    fgcolor = eventStyle.ThirdOptionForeColor;
                    return { bgcolor, fgcolor };
                }
                if (eventStyle.OptionBackColor) {
                    bgcolor = eventStyle.OptionBackColor;
                    fgcolor = eventStyle.OptionForeColor;
                    return { bgcolor, fgcolor };
                }
                bgcolor = 'fadb14';
                fgcolor = '000000';
                return { bgcolor, fgcolor };
            }
        }
    };

    const { bgcolor, fgcolor } = getColors();

    return (
        <EventModelNameSpan>
            <AcknowledgmentTick ackn={isAcknowledgment} />
            <p className={className}>{contactName.toLowerCase()}</p>
            {hasOptionNumber && (
                <OptionNumberDisc $bgcolor={bgcolor} $fgcolor={fgcolor}>
                    {model.OptionNumber < 10 ? model.OptionNumber : 'J'}
                </OptionNumberDisc>
            )}
        </EventModelNameSpan>
    );
};

export { EventModelName };
